import { useState } from 'react';
import type { EmailMessage } from '@epasu/core';
import { FOLLOWUP_STATE_LABELS } from '@epasu/core/constants';
import { crm } from '@/lib/crm-client';
import { Badge } from '@/components/ui/badge';

/**
 * Die Plakette an einer Erstansprache im Verlauf.
 *
 * Eine Zeile unter der Kopfzeile der Nachricht, mehr nicht: Was hier steht,
 * beantwortet die Frage „warum erinnert mich das System an diese Mail“ — und
 * bietet die eine Handlung an, die dazugehört, wenn die Antwort lautet: zu
 * Unrecht. Nachgefasst wird nicht hier, sondern auf der Seite „Freigaben“,
 * wo der vorbereitete Entwurf liegt.
 *
 * An allen anderen Nachrichten zeichnet die Plakette nichts.
 */
export function ErstanspracheHinweis({
  message,
  onChanged,
}: {
  message: EmailMessage;
  onChanged?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (message.kind !== 'outreach') return null;

  const widerrufen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await crm.followups.setOutreach(message.id, false);
      onChanged?.();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Ändern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
      <Badge variant="brand">Erstansprache</Badge>

      <span className="text-muted-foreground">{faelligkeit(message)}</span>

      <button
        type="button"
        disabled={busy}
        onClick={() => void widerrufen()}
        className="text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
      >
        Doch keine Erstansprache
      </button>

      {fehler && <span className="text-danger">{fehler}</span>}
    </div>
  );
}

/**
 * Was als Nächstes ansteht — oder warum nichts mehr ansteht.
 *
 * Ohne Fälligkeit hat die Uhr einen Endzustand erreicht; dann steht dort
 * dieser statt eines Datums, sonst sähe die Nachricht aus, als sei etwas
 * verlorengegangen.
 */
function faelligkeit(message: EmailMessage): string {
  if (!message.followupDueAt) {
    const zustand = message.followupState
      ? FOLLOWUP_STATE_LABELS[message.followupState]
      : null;
    return zustand ? `· ${zustand}` : '· keine Erinnerung';
  }

  // Eine gesetzte Fälligkeit heißt immer: die Uhr (`naechsteWartezeit` in
  // engine/followups.ts) hat für genau diese Stufe eine echte nächste Stufe
  // gefunden — mit Kampagne aus deren eigenen Schritten, sonst aus
  // FOLLOWUP_STEP_DAYS. Die Stufenzahl braucht deshalb keinen eigenen
  // Deckel: stünde hier keine reale nächste Stufe an, wäre `followupDueAt`
  // gar nicht erst gesetzt — genau dieselbe Prüfung, nur einmal statt
  // zweimal getroffen. Bis vor Kurzem stand hier eine Kappung bei
  // `FOLLOWUP_MAX_STEP` (3): richtig für die alte feste 3/7/14-Kadenz, aber
  // falsch für eine Kampagne mit mehr Stufen — die vierte Nachfass-Stufe
  // verlor dort ihre Nummer und zeigte nur noch ein Datum, obwohl die Uhr
  // echt weiterlief (derselbe Fehler wie an `nextStep` in
  // core/src/services/followups.ts, siehe dort).
  const naechste = (message.followupStep ?? 0) + 1;
  const datum = new Date(message.followupDueAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  });

  return `· ${naechste}. Nachfass fällig am ${datum}`;
}
